import { AxiosInstance } from "axios";
import { ControlContext } from "context/Controls";
import { useContext } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useClient } from "utils/client";
import { useTranslation } from "react-i18next";

export enum SubscriptionPlan {
  Free = "free",
  ProMd = "pro-md",
  ProLg = "pro-lg",
}

export enum BillingFrequency {
  Month = "month",
  Year = "year",
}

export enum PaymentMethod {
  Card = "card",
  Sepa = "sepa",
  Invoice = "invoice",
}

export type SubscriptionUIState =
  | "free"
  | "trial_active"
  | "active"
  | "cancelling"
  | "payment_pending";

export interface BillingDetails {
  companyName: string;
  street: string;
  city: string;
  postalCode: string;
  country: string;
  vatId?: string;
}

export interface SubscriptionResponse {
  plan: SubscriptionPlan;
  frequency: BillingFrequency;
  paid: boolean;
  cancelled: boolean;
  cancelAtPeriodEnd: boolean;
  trailEnd: number;
  paymentMethod: PaymentMethod;
}

export const getSubscriptionState = (
  sub: SubscriptionResponse | null
): SubscriptionUIState => {
  if (!sub) {
    return "free";
  }

  if (sub.plan === SubscriptionPlan.Free) {
    if (sub.trailEnd > Date.now() / 1000) return "trial_active";
    return "free";
  }

  if (sub.cancelAtPeriodEnd) return "cancelling";
  if (!sub.paid && sub.trailEnd < Date.now() / 1000) return "payment_pending";
  return "active";
};

const fetchSubscription = async (
  client: AxiosInstance
): Promise<SubscriptionResponse | null> => {
  try {
    const { data } = await client.get("/stripe/subscription");
    return data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const useSubscription = () => {
  const client = useClient();

  const { data: subscription, isLoading } = useQuery<SubscriptionResponse | null>(
    ["stripe", "subscription"],
    () => fetchSubscription(client),
    {
      retry: (failureCount, error: any) => {
        if (error.response?.status === 404) return false;
        return failureCount < 3;
      },
    }
  );

  const uiState = getSubscriptionState(subscription ?? null);

  return { subscription, isLoading, uiState };
};

export const useStartCheckout = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { displayNotification } = useContext(ControlContext);
  const { t } = useTranslation();

  const { mutate, isLoading } = useMutation(
    (params: {
      plan: SubscriptionPlan;
      frequency: BillingFrequency;
      paymentMethod: PaymentMethod;
      billing?: BillingDetails;
    }) =>
      client.post("/stripe/checkout", {
        plan: params.plan,
        frequency: params.frequency,
        paymentMethod: params.paymentMethod,
        ...(params.billing && { billing: params.billing }),
      }),
    {
      onSuccess: (response, variables) => {
        const url: string = response.data;

        if (url === "invoice_sent") {
          queryClient.invalidateQueries(["stripe", "subscription"]);
          queryClient.invalidateQueries("account");
          displayNotification!(
            t("settings.billing.invoice_sent"),
            "",
            "success"
          );
          return;
        }

        if (variables.paymentMethod === PaymentMethod.Invoice) {
          queryClient.invalidateQueries(["stripe", "subscription"]);
          queryClient.invalidateQueries("account");
          window.open(url, "_blank");
          displayNotification!(
            t("settings.billing.invoice_opened"),
            "",
            "success"
          );
          return;
        }

        window.location.href = url;
      },
      onError: (e) => {
        displayNotification!(
          t("settings.billing.checkout_error"),
          // @ts-ignore
          (e as Error).response?.data || (e as Error).message,
          "error"
        );
      },
    }
  );

  return { startCheckout: mutate, isLoading };
};

export const useOpenBillingPortal = () => {
  const client = useClient();
  const { displayNotification } = useContext(ControlContext);
  const { t } = useTranslation();

  const { mutate, isLoading } = useMutation(
    () => client.post("/stripe/portal"),
    {
      onSuccess: (response) => {
        window.location.href = response.data;
      },
      onError: (e) => {
        displayNotification!(
          t("settings.billing.portal_error"),
          // @ts-ignore
          (e as Error).response?.data || (e as Error).message,
          "error"
        );
      },
    }
  );

  return { openBillingPortal: mutate, isLoading };
};

export const useChangeSubscription = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { displayNotification } = useContext(ControlContext);
  const { t } = useTranslation();

  const { mutate, isLoading } = useMutation(
    (params: { plan: SubscriptionPlan; frequency: BillingFrequency }) =>
      client.post("/stripe/subscription/change", {
        plan: params.plan,
        frequency: params.frequency,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["stripe", "subscription"]);
        displayNotification!(t("settings.billing.change_success"), "", "success");
      },
      onError: (e) => {
        displayNotification!(
          t("settings.billing.checkout_error"),
          // @ts-ignore
          (e as Error).response?.data || (e as Error).message,
          "error"
        );
      },
    }
  );

  return { changeSubscription: mutate, isLoading };
};

export const useCancelSubscription = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { displayNotification } = useContext(ControlContext);
  const { t } = useTranslation();

  const { mutate, isLoading } = useMutation(
    () => client.post("/stripe/subscription/cancel"),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["stripe", "subscription"]);
        displayNotification!(t("settings.billing.cancel_success"), "", "success");
      },
      onError: (e) => {
        displayNotification!(
          t("settings.billing.checkout_error"),
          // @ts-ignore
          (e as Error).response?.data || (e as Error).message,
          "error"
        );
      },
    }
  );

  return { cancelSubscription: mutate, isLoading };
};
